(() => {
  const SELECTOR = '[data-calinfi-instagram]';
  const controllers = new WeakMap();

  const text = (value, fallback = '') => (typeof value === 'string' ? value : fallback);

  const escapeHtml = (value) =>
    text(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const parseLimit = (value) => {
    const parsed = Number.parseInt(value || '', 10);
    return Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), 12) : 12;
  };

  const liveCardMarkup = (item) => {
    const caption = text(item.caption);
    const imageUrl = text(item.imageUrl || item.image_url);
    const videoUrl = text(item.videoUrl || item.video_url);
    const mediaType = text(item.mediaType || item.media_type, 'IMAGE').toUpperCase();
    const permalink = text(item.permalink);
    const alt = caption || 'CALINFI Instagram post';

    if (!imageUrl || !permalink) return '';

    const mediaMarkup =
      mediaType === 'VIDEO' && videoUrl
        ? `
          <video
            class="calinfi-instagram-showcase__image calinfi-instagram-showcase__video"
            src="${escapeHtml(videoUrl)}"
            poster="${escapeHtml(imageUrl)}"
            aria-label="${escapeHtml(alt)}"
            autoplay
            muted
            loop
            playsinline
            preload="metadata"
          ></video>
        `
        : `<img class="calinfi-instagram-showcase__image" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(alt)}" loading="lazy">`;

    return `
      <article class="calinfi-instagram-showcase__card" data-instagram-slide>
        <a class="calinfi-instagram-showcase__media" href="${escapeHtml(permalink)}" aria-label="${escapeHtml(alt)}" target="_blank" rel="noopener">
          ${mediaMarkup}
        </a>
      </article>
    `;
  };

  const initSection = (section) => {
    if (!section || section.dataset.instagramInitialized === 'true') return;

    const scroller = section.querySelector('[data-instagram-scroller]');
    const track = section.querySelector('.calinfi-instagram-showcase__track');
    const status = section.querySelector('[data-instagram-status]');
    const prev = section.querySelector('[data-instagram-prev]');
    const next = section.querySelector('[data-instagram-next]');

    if (!scroller || !track) return;

    section.dataset.instagramInitialized = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    controllers.set(section, controller);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let slides = Array.from(section.querySelectorAll('[data-instagram-slide]'));

    const refreshSlides = () => {
      slides = Array.from(section.querySelectorAll('[data-instagram-slide]'));
      return slides;
    };

    const setStatus = (message, visible = false) => {
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !visible;
    };

    const getStep = () => {
      refreshSlides();
      if (slides.length === 0) return scroller.clientWidth;
      if (slides.length < 2) return slides[0].getBoundingClientRect().width;

      const first = slides[0].getBoundingClientRect();
      const second = slides[1].getBoundingClientRect();

      return Math.max(second.left - first.left, first.width);
    };

    const getMaxScroll = () => Math.max(scroller.scrollWidth - scroller.clientWidth, 0);

    const updateControls = () => {
      refreshSlides();
      const maxScroll = getMaxScroll();
      const atStart = scroller.scrollLeft <= 2;
      const atEnd = scroller.scrollLeft >= maxScroll - 2;
      const hasScrollableSlides = slides.length > 1 && maxScroll > 2;

      if (prev) prev.disabled = !hasScrollableSlides || atStart;
      if (next) next.disabled = !hasScrollableSlides || atEnd;
    };

    const scrollByDirection = (direction) => {
      refreshSlides();
      if (slides.length === 0) return;

      const behavior = prefersReducedMotion.matches ? 'auto' : 'smooth';
      const nextLeft = Math.min(Math.max(scroller.scrollLeft + getStep() * direction, 0), getMaxScroll());

      scroller.scrollTo({
        left: nextLeft,
        behavior,
      });
    };

    prev?.addEventListener('click', () => scrollByDirection(-1), { signal });
    next?.addEventListener('click', () => scrollByDirection(1), { signal });
    scroller.addEventListener('scroll', updateControls, { signal, passive: true });

    window.addEventListener('resize', updateControls, { signal });

    const loadLiveFeed = async () => {
      if (section.dataset.instagramLiveEnabled !== 'true') return;

      const endpoint = text(section.dataset.instagramFeedEndpoint).trim();
      if (!endpoint) return;

      const loadingText = text(section.dataset.instagramLoadingText, 'Loading latest Instagram posts');
      setStatus(loadingText, slides.length === 0);

      try {
        const url = new URL(endpoint, window.location.href);

        if (!url.searchParams.has('limit')) {
          url.searchParams.set('limit', String(parseLimit(section.dataset.instagramFeedLimit)));
        }

        const response = await fetch(url.toString(), {
          signal,
          headers: {
            Accept: 'application/json',
          },
        });
        const payload = await response.json();
        const items = Array.isArray(payload.items) ? payload.items : [];
        const markup = items.map(liveCardMarkup).filter(Boolean).join('');

        if (!response.ok || payload.ok !== true || !markup) {
          throw new Error(payload.message || 'Instagram feed unavailable.');
        }

        track.innerHTML = markup;
        refreshSlides();
        scroller.scrollTo({ left: 0, behavior: 'auto' });
        setStatus('', false);
        updateControls();
      } catch (error) {
        if (error?.name === 'AbortError') return;

        setStatus('', false);
        updateControls();
      }
    };

    scroller.scrollTo({ left: 0, behavior: 'auto' });
    updateControls();
    loadLiveFeed();
  };

  document.querySelectorAll(SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);

    initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
    const controller = controllers.get(section);

    controller?.abort();
    controllers.delete(section);

    if (section) delete section.dataset.instagramInitialized;
  });
})();
